import './Login.css';
import { useState } from 'react';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import LoginService from '../../services/LoginService';
import {
   LOCAL_LOGIN_ASSETS,
   TENANT_LOGIN_ASSETS,
   markTenantAssetMissing,
   resolveLoginAssetsSync,
} from '../../misc/TenantAssets';

const { Text, Title, Paragraph } = Typography;

export default function ForgotPassword() {
   const version = `${process.env.REACT_APP_VERSION}`;
   const [form] = Form.useForm();
   const [loading, setLoading] = useState(false);
   // Branding is resolved synchronously from the tenant subdomain, so the very
   // first paint already uses the tenant assets instead of the bundled defaults.
   const [schoolPhotoSrc, setSchoolPhotoSrc] = useState(() => resolveLoginAssetsSync().photo);

   const [logoSrc, setLogoSrc] = useState(() => resolveLoginAssetsSync().logo);
   const schoolPhotoAlt = 'School learning environment';

   // Fall back to the bundled image when the tenant has not uploaded its own,
   // and record the miss so later mounts skip the doomed request entirely.
   const handleLogoSrcError = () => {
      markTenantAssetMissing(TENANT_LOGIN_ASSETS.logo);
      setLogoSrc((prev) => (prev === LOCAL_LOGIN_ASSETS.logo ? prev : LOCAL_LOGIN_ASSETS.logo));
   };

   const handleSchoolPhotoSrcError = () => {
      markTenantAssetMissing(TENANT_LOGIN_ASSETS.photo);
      setSchoolPhotoSrc((prev) =>
         prev === LOCAL_LOGIN_ASSETS.photo ? prev : LOCAL_LOGIN_ASSETS.photo
      );
   };

   const moduleNotAvailable = () => {
      Modal.info({
         title: 'Brainee LMS',
         content: 'The password has been emailed to you.',
         centered: true,
      });
   };

   const onFinish = (values) => {
      setLoading(true);

      LoginService.forgotPassword(values.email)
         .then(() => {
            moduleNotAvailable();
            setLoading(false);
            form.resetFields();
         })
         .catch(() => {
            setLoading(false);
         });
   };

   return (
      <div className="login-page">
         <div className="login-shell">
            <section className="login-hero">
               <div className="login-hero__backdrop" />
               <div className="login-hero__content">
                  <div className="login-hero__brand">
                     <div className="login-hero__brand-mark">
                        <img
                           src={logoSrc}
                           alt="School logo"
                           onError={handleLogoSrcError}
                        />
                     </div>
                     <div>
                        <Text className="login-hero__eyebrow">Account Recovery</Text>
                        <Title level={1} className="login-hero__title">
                           Reset access to your academic workspace
                        </Title>
                        <Paragraph className="login-hero__text">
                           Enter the email linked to your account and we will send your password
                           details so you can sign back in securely.
                        </Paragraph>
                     </div>
                  </div>

                  <div className="login-hero__photo">
                     <img
                        src={schoolPhotoSrc}
                        alt={schoolPhotoAlt}
                        onError={handleSchoolPhotoSrcError}
                     />
                  </div>
               </div>
            </section>

            <section className="login-panel">
               <div className="login-panel__card forgot-panel__card">
                  <div className="login-panel__header">
                     <img
                        className="login-panel__logo"
                        src={logoSrc}
                        alt="School logo"
                        onError={handleLogoSrcError}
                     />
                     <Text className="login-panel__kicker">Password Help</Text>
                     <Title level={2} className="login-panel__title">
                        Forgot your password?
                     </Title>
                     <Paragraph className="login-panel__subtitle">
                        Use your registered email address and we will send your password there.
                     </Paragraph>
                  </div>

                  <Form
                     form={form}
                     layout="vertical"
                     className="login-form forgot-form"
                     onFinish={onFinish}
                  >
                     <Form.Item
                        label={<span className="login-form__label">Email address</span>}
                        name="email"
                        rules={[{ required: true, message: 'Please input your email!' }]}
                     >
                        <Input
                           className="login-form__input"
                           size="large"
                           prefix={<MailOutlined />}
                           placeholder="Enter your registered email"
                           disabled={loading}
                        />
                     </Form.Item>

                     <Paragraph className="forgot-form__hint">
                        We&apos;ll send your password to the email address associated with your
                        account.
                     </Paragraph>

                     <Button
                        className="login-form__button"
                        size="large"
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                     >
                        {loading ? 'Sending...' : 'Send Password'}
                     </Button>

                     <Button
                        className="forgot-form__secondary ant-btn"
                        size="large"
                        block
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                           window.location.href = '/';
                        }}
                     >
                        Back to Sign In
                     </Button>
                  </Form>

                  <div className="login-panel__footer">
                     <span>Brainee LMS</span>
                     <span>Powered by Campus Cloud IT Consultancy - Ver.{version}</span>
                  </div>
               </div>
            </section>
         </div>
      </div>
   );
}
